/**
 * Multi-leg basket EXECUTION — Basket brief #5. 🔴 The money path.
 *
 * Pure: ordering, pre-flight, outcome classification and the report model. No
 * DOM, no clock, no network — live.html does the placing, this decides what may
 * be placed, in what order, and what a response MEANS. Every rule below is a
 * ruling in briefs/BASKET_EXECUTE_RULINGS.md, reviewed before implementation
 * because these decide what happens to real positions when a placement fails
 * half-way.
 *
 * The design constraint that shapes everything: A BASKET IS NOT ATOMIC. There
 * is no all-or-nothing at the broker. N legs are N sequential calls to the
 * existing /api/live/order — reused rather than replaced by a bulk endpoint,
 * because that endpoint already carries freeze-qty chunking, per-child
 * idempotency ids, the race adoption, the circuit breaker and the
 * ambiguous-vs-definitive error distinction, each of which exists because an
 * incident produced it.
 */

import { legQty, type BasketLeg } from "./leg";
import { brokerSegment } from "../symbols";

// ── D1: placement order ──────────────────────────────────────────────────────

/**
 * BUY legs before SELL legs, stable within each group.
 *
 * The intermediate position must never be riskier than the intended final one.
 * Legs are placed over hundreds of milliseconds, and between the first fill and
 * the last the position is NOT the strategy the user chose. Buy-first means a
 * failure part-way leaves a long option — bounded, already paid for. Sell-first
 * would leave a NAKED SHORT, unbounded, held until someone notices.
 *
 * The cost is real and is handled in pre-flight, not discovered mid-basket: the
 * protective legs are paid for before any short arrives to earn the offsetting
 * margin benefit, so the requirement DURING placement exceeds the final netted
 * figure (see peakMarginLegs).
 */
export function placementOrder(legs: BasketLeg[]): BasketLeg[] {
  const buys = (legs || []).filter(l => l && l.side === "BUY");
  const sells = (legs || []).filter(l => l && l.side === "SELL");
  return [...buys, ...sells];
}

/**
 * The prefix whose margin is the PEAK under buy-first: all the buys, no sells.
 *
 * /api/margin/basket reports the basket as a completed whole, with the hedge
 * benefit of the offsetting legs already applied. That is the steady state, not
 * the requirement while placing. Pre-flight must check both and require funds
 * for the larger, or it passes baskets that cannot actually be placed buy-first
 * and the failure surfaces mid-basket — exactly what pre-flight exists to stop.
 */
export function peakMarginLegs(legs: BasketLeg[]): BasketLeg[] {
  return (legs || []).filter(l => l && l.side === "BUY");
}

// ── D2a: identical legs would collide with the server's duplicate guard ─────

export interface MergedLeg {
  leg: BasketLeg;
  /** Ids of the original legs folded into this one (length > 1 = merged). */
  sourceIds: string[];
  lots: number;
}

/**
 * Fold legs identical in (symbol, side) into one, summing lots.
 *
 * /api/live/order refuses an identical (contract, side, chunk-qty, price) from
 * the same user inside DUPLICATE_ORDER_WINDOW_SECONDS — which DEFAULTS TO ONE
 * SECOND, while basket legs go out back-to-back in well under that. So a basket
 * holding the same contract twice on the same side would place leg A and get a
 * 409 on leg B, stopping half-placed.
 *
 * The guard is correct and must not be weakened; the basket must not create the
 * collision. Merging is shown in the confirmation modal so the user sees what is
 * actually sent — this must never be a silent rewrite of their basket.
 */
export function mergeIdenticalLegs(legs: BasketLeg[]): MergedLeg[] {
  const out: MergedLeg[] = [];
  const index = new Map<string, MergedLeg>();
  for (const leg of (legs || []).filter(Boolean)) {
    const key = `${leg.symbol}|${leg.side}`;
    const hit = index.get(key);
    if (hit) {
      hit.lots += Number(leg.lots) || 0;
      hit.sourceIds.push(leg.id);
      hit.leg = { ...hit.leg, lots: hit.lots };
    } else {
      const entry: MergedLeg = {
        leg: { ...leg },
        sourceIds: [leg.id],
        lots: Number(leg.lots) || 0,
      };
      index.set(key, entry);
      out.push(entry);
    }
  }
  return out;
}

// ── D2: pre-flight ──────────────────────────────────────────────────────────

export type BlockCode =
  | "flag-off"
  | "in-flight"
  | "empty"
  | "unresolved"
  | "bad-exchange"
  | "stale-ltp"
  | "no-ltp"
  | "market-closed"
  | "insufficient-funds"
  | "bad-qty";

export interface PreflightBlock {
  code: BlockCode;
  /** Operator-facing sentence. Must name what is wrong, not just that it is. */
  message: string;
  /** Legs implicated, when the block is per-leg. */
  legIds?: string[];
}

export interface LegPrice {
  ltp: number | null;
  /** Age of that price in seconds; null = never ticked. */
  ageSeconds: number | null;
}

export interface PreflightInput {
  legs: BasketLeg[];
  flagOn: boolean;
  inFlight: boolean;
  marketOpen: boolean;
  /** Per-leg price + staleness, by leg id. */
  priceFor: (leg: BasketLeg) => LegPrice;
  /** True when the leg has a broker tsym (re-resolved at click). */
  resolved: (leg: BasketLeg) => boolean;
  maxLtpAgeSeconds: number;
  /** Cash available now: cash + payin - marginused. */
  funds: number;
  /** Margin for the whole basket — the steady state. */
  basketMargin: number;
  /** Margin for the all-buys prefix — the peak under buy-first (D1a). */
  peakMargin: number;
  /** Fraction of funds we are willing to commit (existing sizing uses 0.9). */
  utilisation?: number;
}

export interface PreflightResult {
  ok: boolean;
  blocks: PreflightBlock[];
  /** Merged, ordered legs that WOULD be placed. Empty when not ok. */
  plan: MergedLeg[];
  /** The figure funds are actually checked against. */
  requiredMargin: number;
  peakMargin: number;
  basketMargin: number;
}

const inr = (n: number) =>
  "₹" + Math.round(Math.abs(n)).toLocaleString("en-IN");

/**
 * Explain a funds refusal caused by buy-first (D1, amendment 2).
 *
 * Some legitimately affordable NET-CREDIT baskets are refused because the peak
 * during placement exceeds the final requirement. Without saying why, that reads
 * as a bug — the user can see the final margin is affordable.
 */
export function fundsMessage(peak: number, basket: number, funds: number): string {
  const required = Math.max(peak, basket);
  const base = `Not enough funds: needs ${inr(required)}, available ${inr(funds)}.`;
  if (peak > basket) {
    return base + ` This basket needs ${inr(peak)} DURING placement (${inr(basket)} once complete)`
      + " because the protective legs are bought first — the offsetting margin benefit only"
      + " arrives once the short legs are placed. Buying first is deliberate: it means a failure"
      + " part-way leaves a long option rather than a naked short.";
  }
  return base;
}

/**
 * Explain a staleness refusal, distinguishing an illiquid strike from a dead
 * feed (amendment 1).
 *
 * A far-OTM strike can genuinely go minutes without a tick on a quiet afternoon,
 * and that is not the same fault as the feed being down. If EVERY leg is stale
 * the feed is the likely cause; if some are fresh, the stale ones are simply not
 * trading. Either way the leg and its age are named, never just "stale prices".
 */
export function stalenessMessage(
  stale: Array<{ leg: BasketLeg; ageSeconds: number }>, total: number, maxAge: number,
): string {
  const named = stale
    .map(s => `${s.leg.symbol} (last tick ${Math.round(s.ageSeconds)}s ago)`)
    .join(", ");
  const allStale = stale.length === total && total > 1;
  const cause = allStale
    ? "EVERY leg is stale, which usually means the FEED is down rather than the strikes being quiet"
      + " — check the feed status before assuming illiquidity."
    : "Other legs are ticking normally, so this looks like an ILLIQUID STRIKE rather than a feed"
      + " fault. A far-OTM strike can go minutes without a trade.";
  return `Prices older than ${Math.round(maxAge)}s cannot price a basket order: ${named}. ${cause}`;
}

/**
 * Evaluate every gate. A basket that fails ANY gate places NOTHING — the single
 * most valuable property in this brief.
 *
 * All gates are evaluated (not short-circuited) so the operator sees everything
 * wrong at once instead of fixing one problem per attempt.
 */
export function preflight(input: PreflightInput): PreflightResult {
  const blocks: PreflightBlock[] = [];
  const legs = (input.legs || []).filter(Boolean);
  const util = input.utilisation == null ? 0.9 : input.utilisation;
  const requiredMargin = Math.max(input.peakMargin || 0, input.basketMargin || 0);

  if (!input.flagOn) {
    blocks.push({ code: "flag-off", message: "Basket execution is disabled (BASKET_EXECUTE is off)." });
  }
  if (input.inFlight) {
    blocks.push({
      code: "in-flight",
      message: "A basket submission is already in progress — only one at a time.",
    });
  }
  if (!legs.length) {
    blocks.push({ code: "empty", message: "No legs to place." });
  }
  if (!input.marketOpen) {
    blocks.push({ code: "market-closed", message: "The market is closed — nothing was placed." });
  }

  // Fail CLOSED on an unresolvable contract. Precedent: the SENSEX weekly
  // incident (24ae990), where a plausible substitution hid a missing contract.
  const unresolved = legs.filter(l => !input.resolved(l));
  if (unresolved.length) {
    blocks.push({
      code: "unresolved",
      legIds: unresolved.map(l => l.id),
      message: `${unresolved.length} leg(s) do not resolve to a tradeable contract at the broker `
        + `(${unresolved.map(l => l.symbol).join(", ")}). Nothing was placed, and no substitute `
        + "contract was used.",
    });
  }

  const noPrice: BasketLeg[] = [];
  const stale: Array<{ leg: BasketLeg; ageSeconds: number }> = [];
  for (const leg of legs) {
    const p = input.priceFor(leg);
    if (p == null || p.ltp == null || !(p.ltp > 0)) { noPrice.push(leg); continue; }
    if (p.ageSeconds != null && p.ageSeconds > input.maxLtpAgeSeconds) {
      stale.push({ leg, ageSeconds: p.ageSeconds });
    }
  }
  if (noPrice.length) {
    blocks.push({
      code: "no-ltp",
      legIds: noPrice.map(l => l.id),
      message: `No live price for ${noPrice.map(l => l.symbol).join(", ")} — a limit price cannot be `
        + "derived without one, so nothing was placed.",
    });
  }
  if (stale.length) {
    blocks.push({
      code: "stale-ltp",
      legIds: stale.map(s => s.leg.id),
      message: stalenessMessage(stale, legs.length, input.maxLtpAgeSeconds),
    });
  }

  // ORDER-1 — a leg whose exchange does not map to a broker segment must stop
  // the basket here, before anything is placed. The defect this replaces
  // DEFAULTED to "NFO", which is a real exchange: every SENSEX order was sent to
  // NFO and rejected. An exchange that cannot be derived is refused, never
  // guessed — a wrong exchange is a wrong instrument.
  const badExchange = legs.filter(l => !brokerSegment(l.exchange));
  if (badExchange.length) {
    blocks.push({
      code: "bad-exchange",
      legIds: badExchange.map(l => l.id),
      message: `${badExchange.length} leg(s) carry an exchange that does not map to a broker `
        + `segment (${badExchange.map(l => `${l.symbol}: ${l.exchange || "(empty)"}`).join(", ")}). `
        + "Nothing was placed — an order is never sent to a guessed exchange.",
    });
  }

  const badQty = legs.filter(l => !(legQty(l) > 0));
  if (badQty.length) {
    blocks.push({
      code: "bad-qty",
      legIds: badQty.map(l => l.id),
      message: `${badQty.length} leg(s) have no lot size or zero quantity — resolve the contract first.`,
    });
  }

  if (legs.length && requiredMargin > (input.funds || 0) * util) {
    blocks.push({
      code: "insufficient-funds",
      message: fundsMessage(input.peakMargin || 0, input.basketMargin || 0, (input.funds || 0) * util),
    });
  }

  const ok = blocks.length === 0;
  return {
    ok,
    blocks,
    plan: ok ? mergeIdenticalLegs(placementOrder(legs)) : [],
    requiredMargin,
    peakMargin: input.peakMargin || 0,
    basketMargin: input.basketMargin || 0,
  };
}

// ── §0: what a response MEANS ───────────────────────────────────────────────

export type LegOutcome =
  /** Broker accepted every chunk. NOT "filled" — see the note below. */
  | "accepted"
  /** Some chunks live at the broker, the rest not attempted. STOP. */
  | "partial"
  /** Definitively refused; nothing of this leg is live. STOP. */
  | "rejected"
  /** The call did not resolve — the leg MAY be live. STOP, verify. */
  | "unknown"
  /** Never sent, because an earlier leg stopped the basket. */
  | "not-attempted";

export interface LegResult {
  legId: string;
  symbol: string;
  side: string;
  qty: number;
  outcome: LegOutcome;
  detail: string;
  clientOrderId?: string;
}

/** Outcomes that must halt the basket. */
export function isStop(outcome: LegOutcome): boolean {
  return outcome !== "accepted";
}

/**
 * Classify one /api/live/order response.
 *
 * THE TRAP: a partially-filled leg comes back as **HTTP 200** with
 * `status: "partial"` (routers/live_trading.py:299-359 — some chunks live at the
 * broker, the remainder deliberately not attempted). A natural `if (res.ok)`
 * loop reads that as success and keeps placing. It is the highest-risk detail in
 * this feature, which is why classification is a tested function rather than an
 * inline condition.
 *
 * `status: "duplicate"` is an idempotent replay. Mid-basket that means the ids
 * were reused, which should not happen — treat it as a stop rather than assume.
 */
/**
 * The broker's reason for a rejection, as text an operator can act on.
 *
 * ORDER-1 defect 2. This was `String(body.detail || ...)`, and `String()` on an
 * object yields **"[object Object]"** — which is what the operator was shown for
 * an entire SENSEX expiry session while defect 1 rejected every order. The
 * broker almost certainly said why; the UI threw it away. A rejection reason the
 * operator cannot read is the same class as a silent failure.
 *
 * FastAPI's HTTPException detail is not required to be a string: a structured
 * broker error is an object, and a request-validation error is a LIST of objects
 * (`[{loc, msg, type}, ...]`). The existing pattern in live.html (:2505, :3316,
 * :4107) handles the object case; the array case would fall through it to a bare
 * "error", so it is handled here too.
 */
export function readDetail(
  body: { detail?: unknown } | null | undefined, status: number,
): string {
  const detail = (body || {}).detail;
  const fallback = `HTTP ${status}`;
  if (typeof detail === "string") return detail || fallback;
  if (detail == null) return fallback;
  if (Array.isArray(detail)) {
    const parts = detail.map(one => readDetail({ detail: one }, status))
      .filter(s => s && s !== fallback);
    return parts.length ? parts.join("; ") : fallback;
  }
  if (typeof detail === "object") {
    const d = detail as Record<string, unknown>;
    // `msg` is FastAPI's validation field; `message`/`emsg` are the broker's.
    for (const key of ["message", "msg", "emsg", "error", "reason", "detail"]) {
      const v = d[key];
      if (typeof v === "string" && v) return v;
    }
    // Nothing recognisable — show the payload rather than "[object Object]".
    // Unreadable JSON still beats a string that names no problem at all.
    try {
      const json = JSON.stringify(detail);
      if (json && json !== "{}") return json;
    } catch (e) { /* circular — fall through */ }
    return fallback;
  }
  return String(detail);
}

export function classifyResponse(
  res: { ok: boolean; status: number } | null | undefined,
  body: Record<string, unknown> | null | undefined,
): { outcome: LegOutcome; detail: string } {
  if (res == null) {
    return {
      outcome: "unknown",
      detail: "No response from the server — this leg MAY be live at the broker. "
        + "Nothing further was placed. Verify in the order book before retrying.",
    };
  }
  if (res.ok) {
    const status = String((body || {}).status || "");
    if (status === "partial") {
      const failed = (body || {}).failed_chunk as { index?: number; qty?: number } | undefined;
      return {
        outcome: "partial",
        detail: `Partially placed: some child orders are LIVE at the broker and chunk `
          + `${failed && failed.index ? failed.index : "?"} was rejected; the rest were not `
          + "attempted. The basket stopped here.",
      };
    }
    if (status === "duplicate") {
      return {
        outcome: "unknown",
        detail: "The broker returned an idempotent replay for this leg — it was already submitted. "
          + "The basket stopped; check the order book.",
      };
    }
    return {
      outcome: "accepted",
      detail: "Accepted by the broker (status OPEN). A fill is confirmed only by the order book.",
    };
  }
  const detail = readDetail((body || {}) as { detail?: unknown }, res.status);
  if (res.status === 409) {
    return { outcome: "rejected", detail: `Blocked as a duplicate: ${detail}. Nothing was placed for this leg.` };
  }
  if (res.status === 503) {
    return {
      outcome: "rejected",
      detail: "The broker order channel is unavailable (circuit open). Nothing was placed for this leg.",
    };
  }
  return { outcome: "rejected", detail };
}

// ── D4: what may be claimed ─────────────────────────────────────────────────

export interface BasketReport {
  results: LegResult[];
  accepted: number;
  total: number;
  /** True when anything is live at the broker but the basket did not complete. */
  partial: boolean;
  /** True when at least one leg's state is genuinely unknown. */
  needsVerification: boolean;
  headline: string;
}

/**
 * Build the outcome report.
 *
 * NEVER "Basket placed ✓". There is no atomicity at the broker and a checkmark
 * could equally describe 5 of 7. The headline is always "N of M legs accepted",
 * and the word FILLED never appears — a 200 means the broker ACCEPTED the order
 * (status OPEN); fills come only from the reconcile path that owns broker truth
 * (the 2026-07-27 no-optimistic-fill rule).
 */
export function buildReport(results: LegResult[]): BasketReport {
  const list = results || [];
  const accepted = list.filter(r => r.outcome === "accepted").length;
  const total = list.length;
  const anyLive = list.some(r => r.outcome === "accepted" || r.outcome === "partial");
  const complete = accepted === total && total > 0;
  const needsVerification = list.some(r => r.outcome === "unknown" || r.outcome === "partial");
  return {
    results: list,
    accepted,
    total,
    partial: anyLive && !complete,
    needsVerification,
    headline: complete
      ? `All ${total} legs accepted — fills confirm in the order book.`
      : `${accepted} of ${total} legs accepted.`,
  };
}

/** One id per leg, minted at the click. Fits the 60-char cap with room for the
 *  server's "#<n>" child suffix (routers/live_trading.py:136-140). */
export function mintClientOrderIds(count: number, token: string): string[] {
  return Array.from({ length: count }, (_, i) => `bk-${token}-${i + 1}`);
}
